import { getProp } from "../tools/any.tools";
import { getVOFValue, ValueOrFunction } from "../tools/object.tools";
import { DataSource, DataSourceItem } from "./dataSource";

/**
 * Default data source implementation for a simple list of objects.
 * !important T must be an object (typage not really correct here) see with AMT
 */
export class ObjectDataSource<T, K extends keyof T = keyof T, V extends T[K] = T[K], DataSourceContext = void> implements DataSource<T, V, DataSourceContext> {

      /** Full list of items. May be filtered by the optional filterItems before being returned */
    private _dataSourceItems: DataSourceItem<T>[] = [];

    /** Field value of item in DataSourceItem */
    private _valueField: string | number;

    /** Label value of item in DataSourceItem */
    private _labelField: string;

    /** Text for 'null' value when display item as string */
    private _textForNull?: string;

    /** Text for 'undefined' value when display item as string */
    private _textForUndefined?: string;

    /** Text when no item found for displayed value of dataSourceItem */
    private _textForItemNoFound?: string;

    /** Store label of datasource */
    public _label?: ValueOrFunction<string>;

    /** Optional filter applied on both getItems and getObjects */
    private _filterItems?: (item: T | undefined, context?: DataSourceContext) => boolean;

    /** Setter for label of datasource */
    public set label(value: ValueOrFunction<string>) {
        this._label = value;
    }

    /** Getter for displayable label of datasource */
    public get label(): string | undefined {
        return getVOFValue(this._label);
    }

    /**
     * Constructs a data source for a list of objects
     * @param items List of items of DataSource
     * @param valueField will be used to get the value for the item. This is the value that will be stored to designate the item.
     * @param labelField labelField will be used to get the displayString for the item.
     * @param options customs strings for null and undefined values has you cannot get a field from those values.
     * @param options.textForNull Text for 'null' value when display item as string
     * @param options.textForUndefined Text for 'undefined' value when display item as string
     * @param options.textForItemNoFound Text when no item found for displayed value of dataSourceItem
     * @param options.datasourceLabel Label of displayed to user
     * @param options.filterItems optional filter function for getItems and getObjects
     */
    constructor(
        items: T[],
        valueField: string | number,
        labelField: string,
        options?: {
            textForNull?: string,
            textForUndefined?: string,
            textForItemNoFound?: string,
            datasourceLabel?: ValueOrFunction<string>,
            filterItems?: (item: T | undefined, context?: DataSourceContext) => boolean
        }
    ) {
        this.setData(items);
        this._valueField = valueField;
        this._labelField = labelField;
        this._textForNull = options?.textForNull;
        this._textForUndefined = options?.textForUndefined;
        this._textForItemNoFound = options?.textForItemNoFound;
        this._label = options?.datasourceLabel;
        this._filterItems = options?.filterItems;
    }

    /**
     * Set _dataSourceItems from items list
     * @param items 
     */
    public setData(items: (T | undefined)[]): void {
        this._dataSourceItems = items.map(item => { return { item }; });
    }


    /** Get a string from value */
    public getDisplayString(value: V, itemFromCache?: DataSourceItem<T>): string {
        let displayString: string;
        if (value === undefined) {
            displayString = this._textForUndefined ?? "";
        } else if (value === null) {
            displayString = this._textForNull ?? "";
        } else {
            const dataSourceItem = itemFromCache ?? this.getItemFromValue(value);
            if (dataSourceItem?.item && getProp(dataSourceItem.item, this._labelField)) {
                displayString = String(getProp(dataSourceItem.item, this._labelField)); // We use simple String(X) here because it is simple case, we will use renderer later 
            } else if (this._textForItemNoFound !== undefined) {
                displayString = this._textForItemNoFound;
            } else {
                displayString = "";
            }
        }
        return displayString;

    }

    /** Get a value from DataSource item */
    public getValueFromItem(dataSourceItem: DataSourceItem<T>): V | undefined {
        return dataSourceItem.item ? getProp(dataSourceItem.item, this._valueField) : undefined;
    }

    /** Get a DataSource item from value */
    public getItemFromValue(value: V): DataSourceItem<T> | undefined {
        return this._dataSourceItems.find((d) => {
            if (d.item) {
                const itemValue = getProp(d.item, this._valueField);
                return itemValue === value;
            } else {
                return false;
            }
        });
    }

    /** Get filtered list of DataSource items from context */
    public getItems(context?: DataSourceContext): DataSourceItem<T>[] {
        const itemList: DataSourceItem<T>[] = [];
        for (const item of this._dataSourceItems) {
            if (this._filterItems == null || this._filterItems(item.item, context)) {
                itemList.push(item);
            }
        }
        return itemList;
    }


    /** @inheritdoc */
    public getObjects(context?: DataSourceContext): T[] {
        const objects: T[] = [];
        for (const value of this.getItems(context)) {
            if (value.item != null) {
                objects.push(value.item);
            }
        }
        return objects;
    }
}